import React from 'react';

import {
  Box,
  Container,
  Grid,
  ImageList,
  ImageListItem,
  Link,
  Typography,
} from '@material-ui/core';

import { Image } from '../components';
import { useIsMobile, useIsTablet } from '../hooks/useBreakpoints';
import { makeBlockStyles, useBlockStyles } from '../hooks/useStyles';
import { BlockBuilder, BlockIconGrid, Size } from '../types/_blocks.types';
import { normaliseUrl } from '../utils/url';

const useStyles = makeBlockStyles(() => ({
  root: {
    //
  },
}));

type Props = BlockBuilder<BlockIconGrid>;

const percentageSize = {
  small: '50%',
  medium: '75%',
  large: 'initial',
};

const imageMaxSize = (size: Size | number) => {
  if (typeof size === 'number') return size;

  return percentageSize[size] ?? 'initial';
};

export const IconGridBlock: React.FC<Props> = ({ block, getImageData }) => {
  const {
    title,
    content,
    items,
    align = 'center',
    size,
    imageListMaxWidth,
  } = block;
  const classes = useBlockStyles(useStyles, block);
  const isTablet = useIsTablet();
  const isMobile = useIsMobile();
  const maxCols = block.max ? Number(block.max) : 5;
  const gridCols = isMobile ? 2 : isTablet ? Math.min(3, maxCols) : maxCols;

  const itemsFiltered = items.filter(({ image }) => image && image !== '$');

  return (
    <Box className={classes.boxBlock}>
      <Container className={classes.root}>
        <Grid
          container
          direction="column"
          justifyContent="center"
          alignItems="center"
          spacing={4}
        >
          {(!!title || !!content) && (
            <Grid item xs={10} style={{ textAlign: align }}>
              {!!title && (
                <Typography variant="h3" className={classes.typography}>
                  {title}
                </Typography>
              )}
              {!!content && (
                <>
                  <br />
                  <Typography variant="body1">{content}</Typography>
                </>
              )}
            </Grid>
          )}
          <Grid item container xs={10} justifyContent="center" spacing={2}>
            <ImageList
              cols={gridCols}
              component="div"
              rowHeight="auto"
              style={{
                justifyContent: align,
                ...(imageListMaxWidth && { maxWidth: imageListMaxWidth }),
              }}
            >
              {itemsFiltered.map((item, idx) => (
                <ImageListItem
                  key={idx}
                  component="div"
                  style={{ padding: '8px' }}
                >
                  <Link
                    href={normaliseUrl(item.cta.link)}
                    style={{ padding: '1rem' }}
                  >
                    <Grid container direction="column" justifyContent="center">
                      <Grid
                        item
                        xs={12}
                        style={{
                          textAlign: align,
                          width: 'inherit',
                          height: 'inherit',
                        }}
                      >
                        <Image
                          getImageData={getImageData}
                          src={item.image}
                          alt={item.title || 'image'}
                          options={{
                            width: 'inherit',
                            height: 'inherit',
                            ...(size && {
                              maxWidth: imageMaxSize(size),
                              maxHeight: imageMaxSize(size),
                            }),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} style={{ textAlign: align }}>
                        <Typography
                          variant="caption"
                          align="center"
                          style={{ color: '#000000' }}
                        >
                          <strong>{item.title}</strong>
                        </Typography>
                      </Grid>
                    </Grid>
                  </Link>
                </ImageListItem>
              ))}
            </ImageList>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
